import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Building, Apartment } from '@/types';
import { useTranslation } from 'react-i18next';

interface AddSensorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSensorAdded: () => void;
}

export function AddSensorDialog({ open, onOpenChange, onSensorAdded }: AddSensorDialogProps) {
  const { t } = useTranslation();
  const [deviceId, setDeviceId] = useState('');
  const [name, setName] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const [apartmentId, setApartmentId] = useState('');
  const [location, setLocation] = useState('');
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    if (open) {
      fetchBuildings();
      setDeviceId('');
      setName('');
      setBuildingId('');
      setApartmentId('');
      setLocation('');
    }
  }, [open]);

  React.useEffect(() => {
    if (buildingId) {
      fetchApartments(buildingId);
    } else {
      setApartments([]);
      setApartmentId('');
    }
  }, [buildingId]);

  const fetchBuildings = async () => {
    const { data, error } = await supabase
      .from('buildings')
      .select('*')
      .order('name');
    
    if (data && !error) {
      setBuildings(data);
    }
  };

  const fetchApartments = async (buildingId: string) => {
    const { data, error } = await supabase
      .from('apartments')
      .select('*')
      .eq('building_id', buildingId)
      .order('apartment_number');
    
    if (data && !error) {
      setApartments(data);
    }
  };

  const validateDeviceId = (id: string) => {
    return /^[a-zA-Z0-9]{22}$/.test(id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateDeviceId(deviceId)) {
      toast({
        title: t('dialogs.addSensor.invalidId'),
        description: t('dialogs.addSensor.invalidIdDesc'),
        variant: "destructive",
      });
      return;
    }

    if (!name || !buildingId || !location) {
      toast({
        title: t('dialogs.addSensor.missingInfo'),
        description: t('dialogs.addSensor.missingInfoDesc'),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('temperature_sensors')
        .insert([
          {
            device_id: deviceId,
            name,
            building_id: buildingId,
            apartment_id: apartmentId || null,
            location,
            status: 'offline',
            is_online: false,
          }
        ]);

      if (error) throw error;

      toast({
        title: t('dialogs.addSensor.added'),
        description: t('dialogs.addSensor.addedDesc'),
      });

      onSensorAdded();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t('common.errorTitle'),
        description: t('dialogs.addSensor.addFailed'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('dialogs.addSensor.title')}</DialogTitle>
          <DialogDescription>
            {t('dialogs.addSensor.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="deviceId">{t('dialogs.addSensor.deviceIdLabel')}</Label>
            <Input
              id="deviceId"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value.toUpperCase())}
              placeholder={t('dialogs.addSensor.deviceIdPlaceholder')}
              maxLength={22}
              className={!validateDeviceId(deviceId) && deviceId ? 'border-red-500' : ''}
            />
            <p className="text-xs text-muted-foreground">
              {t('dialogs.addSensor.deviceIdHint')}
            </p>
            {deviceId && !validateDeviceId(deviceId) && (
              <p className="text-xs text-red-500">
                {t('dialogs.addSensor.invalidFormat', { length: deviceId.length })}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">{t('dialogs.addSensor.nameLabel')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('dialogs.addSensor.namePlaceholder')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="building">{t('dialogs.addSensor.buildingLabel')}</Label>
            <Select value={buildingId} onValueChange={setBuildingId}>
              <SelectTrigger>
                <SelectValue placeholder={t('dialogs.addSensor.buildingPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {buildings.map((building) => (
                  <SelectItem key={building.id} value={building.id}>
                    {building.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="apartment">{t('dialogs.addSensor.apartmentLabel')}</Label>
            <Select value={apartmentId} onValueChange={setApartmentId} disabled={!buildingId}>
              <SelectTrigger>
                <SelectValue placeholder={t('dialogs.addSensor.apartmentPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {apartments.map((apartment) => (
                  <SelectItem key={apartment.id} value={apartment.id}>
                    {apartment.apartment_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">{t('dialogs.addSensor.locationLabel')}</Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={t('dialogs.addSensor.locationPlaceholder')}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading || !validateDeviceId(deviceId)}>
              {isLoading ? t('dialogs.addSensor.adding') : t('dialogs.addSensor.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}